import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { eraseFromLocalStorage } from '@src/quince-site-common/src/utils/eraseFromLocalStorageWithExpiry'
import { getFromLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/getFromLocalStorageWithExpiry'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { setInLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/setInLocalStorageWithExpiry'

/**
 * Recommendation/module metadata keyed by variant id for checkout `variant_confirmed` (mirrors findingMethodsLB).
 * Keep in sync with web-checkout/utils/recommendationTabData.ts:RecommendationTabData —
 * the two repos exchange this shape via localStorage.
 */
export type RecommendationTabData = Record<string, string | boolean | number | { i: number; j: number }>

const isObjectRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)

const captureExceptionForModule = (error: unknown) => {
  const quinceError = new QuinceApiError({
    error: error as Error,
    message: 'Error in recommendation tab data storage',
    apiName: 'RecommendationTabData',
  })
  captureException(quinceError, {
    module: MODULES.INTERNAL_ANALYTICS,
    feature: FEATURES.TRACK_INTERNAL_ANALYTICS,
  })
}

/** Merge `context.tabData` with top-level `context.product_position` into one blob (stored under `tabData` on events). */
export function recommendationTabDataFromTrackContext(context?: Record<string, unknown>): RecommendationTabData {
  const td = context?.tabData
  const base: RecommendationTabData =
    td !== undefined && typeof td === 'object' && td !== null && !Array.isArray(td)
      ? { ...(td as RecommendationTabData) }
      : {}
  if (context?.product_position !== undefined) {
    base.product_position = context.product_position as number | { i: number; j: number }
  }
  return base
}

export const getRecommendationTabDataByVariant = (): Record<string, RecommendationTabData | undefined> => {
  try {
    const raw = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.RECOMMENDATION_TAB_DATA_BY_VARIANT_LB)
    if (raw) {
      const parsed: unknown = JSON.parse(raw)
      if (isObjectRecord(parsed)) {
        return parsed as Record<string, RecommendationTabData | undefined>
      }
    }
    return {}
  } catch (e) {
    captureExceptionForModule(e)
    return {}
  }
}

export const getCurrentRecommendationTabData = (): RecommendationTabData | undefined => {
  try {
    const raw = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.CURRENT_RECOMMENDATION_TAB_DATA_LB)
    if (!raw) return undefined
    const parsed: unknown = JSON.parse(raw)
    if (isObjectRecord(parsed) && Object.keys(parsed).length > 0) {
      return parsed as RecommendationTabData
    }
    return undefined
  } catch (e) {
    captureExceptionForModule(e)
    return undefined
  }
}

export const setCurrentRecommendationTabData = (tabData: RecommendationTabData) => {
  if (Object.keys(tabData).length === 0) return
  try {
    setInLocalStorageWithExpiry(ANALYTIC_COOKIES.CURRENT_RECOMMENDATION_TAB_DATA_LB, JSON.stringify(tabData))
  } catch (e) {
    captureExceptionForModule(e)
  }
}

export const clearCurrentRecommendationTabData = () => {
  eraseFromLocalStorage(ANALYTIC_COOKIES.CURRENT_RECOMMENDATION_TAB_DATA_LB)
}

/** Snapshot current recommendation context onto a variant when adding to cart (same moment as setFindingMethodForVariant). */
export const setRecommendationTabDataForVariant = (variantId: string) => {
  const current = getCurrentRecommendationTabData()
  const map = getRecommendationTabDataByVariant()
  if (current && Object.keys(current).length > 0) {
    map[variantId] = { ...current }
  }
  setInLocalStorageWithExpiry(ANALYTIC_COOKIES.RECOMMENDATION_TAB_DATA_BY_VARIANT_LB, JSON.stringify(map))
}

export const clearRecommendationTabDataForVariant = (variantId: string) => {
  const map = getRecommendationTabDataByVariant()
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete map[variantId]
  setInLocalStorageWithExpiry(ANALYTIC_COOKIES.RECOMMENDATION_TAB_DATA_BY_VARIANT_LB, JSON.stringify(map))
}
